// ===== Room Members View (Phase 8: CAP17/18) =====
// 헤더 참가인원(app.js renderChatHeader가 렌더하는 "이름들 · N명")을 클릭하면 참가자
// 명단(기존 GET /api/rooms/:id/members 재사용, 이름/사진 표시) + 방 나가기 진입점을 연다.
// app.js는 이번 phase 범위 밖이라 건드리지 않는다 — 현재 열린 방은 app.js:415에서도
// 이미 외부 참조되는 wsClient._currentSessionId를 GET /api/rooms의 sessionId와 매칭해
// 역으로 찾는다(라운드트립 이후엔 항상 서버 확정값이라 신뢰 가능 — onSessionInfo가
// setCurrentSessionId와 bindRoomHeader를 같은 핸들러에서 함께 호출).
// 모달 프레임워크는 group-create.js/profile-edit.js와 동일한 plugin-restart-modal-* CSS를
// 재사용한다(새 모달 프레임워크 금지).

const RoomMembers = (() => {
  interface RoomSummary extends Record<string, unknown> { id: string; sessionId?: string }
  interface RoomMember extends Record<string, unknown> { actorId?: string; displayName?: string; avatarUrl?: string; mutedAt?: string | null }
  function _isRecord(value: unknown): value is Record<string, unknown> { return !!value && typeof value === "object" && !Array.isArray(value); }
  async function _resolveCurrentRoom() {
    const sessionId = typeof wsClient !== "undefined" && wsClient ? wsClient._currentSessionId : null;
    if (!sessionId) return null;
    try {
      const res = await fetch("/api/rooms");
      if (!res.ok) return null;
      const data: unknown = await res.json();
      if (!_isRecord(data) || data.ok !== true || !Array.isArray(data.rooms)) return null;
      return data.rooms.filter(_isRecord).find((room): room is RoomSummary => typeof room.id === "string" && room.sessionId === sessionId) || null;
    } catch (err) {
      return null;
    }
  }

  async function _fetchMembers(roomId: string): Promise<RoomMember[]> {
    const res = await fetch(`/api/rooms/${roomId}/members`);
    if (!res.ok) return [];
    const data: unknown = await res.json();
    if (!_isRecord(data) || data.ok !== true || !Array.isArray(data.members)) return [];
    return data.members.filter((member): member is RoomMember => _isRecord(member));
  }

  async function _leaveRoom(roomId: string) {
    const res = await fetch(`/api/rooms/${roomId}/members/me`, { method: "DELETE" });
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok || !_isRecord(data) || data.ok !== true) {
      throw new Error(_isRecord(data) && typeof data.error === "string" ? data.error : "leave_failed");
    }
  }

  // Phase 17(bigtionax-chat-reply-reactions-read-receipts): 방별 음소거 토글. PUT은 항상 인증
  // actor 본인 것만 바꾼다(path에 actorId가 없다) — leaveRoom과 동일한 self-scope 관례.
  async function _setMuted(roomId: string, muted: boolean): Promise<void> {
    const res = await fetch(`/api/rooms/${roomId}/mute`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ muted }),
    });
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok || !_isRecord(data) || data.ok !== true) {
      throw new Error(_isRecord(data) && typeof data.error === "string" ? data.error : "mute_update_failed");
    }
  }

  function _renderMuteToggle(roomId: string, initialMuted: boolean) {
    const btn = document.createElement("button");
    btn.className = "agent-btn agent-btn-cancel";
    btn.type = "button";
    let muted = initialMuted;
    const label = () => (muted ? "음소거 해제" : "음소거");
    btn.textContent = label();
    btn.addEventListener("click", async () => {
      const next = !muted;
      try {
        await withLoadingFeedback(btn, () => _setMuted(roomId, next), {
          loadingText: "변경 중...",
        });
        muted = next;
        btn.textContent = label();
      } catch (err) {
        console.error("[room-members] 음소거 변경 실패:", err);
      }
    });
    return btn;
  }

  function _renderMemberRow(member: RoomMember) {
    const row = document.createElement("div");
    row.className = "room-member-row";
    row.style.cssText = "display:flex;align-items:center;gap:8px;padding:6px 2px;";

    const avatar = document.createElement("span");
    avatar.className = "room-member-avatar";
    avatar.style.cssText = "width:28px;height:28px;border-radius:50%;background:var(--bg-tertiary);display:flex;align-items:center;justify-content:center;font-size:12px;overflow:hidden;flex-shrink:0;";

    if (member.avatarUrl) {
      const img = document.createElement("img");
      img.src = member.avatarUrl;
      img.alt = "";
      img.referrerPolicy = "no-referrer";
      img.style.cssText = "width:100%;height:100%;object-fit:cover;";
      img.addEventListener("error", () => img.remove());
      avatar.appendChild(img);
    } else {
      const label = (member.displayName || member.actorId || "?").trim();
      avatar.textContent = label ? label[0].toUpperCase() : "?";
    }

    const name = document.createElement("span");
    name.className = "room-member-name";
    name.textContent = member.displayName || member.actorId || "";

    row.appendChild(avatar);
    row.appendChild(name);
    return row;
  }

  async function _renderNaturalSpeechToggle(roomId: string, errorP: HTMLElement, setConfirmationOpen: (check: () => boolean) => void) {
    const endpoint = `/api/rooms/${roomId}/natural-speech`;
    let initial: unknown;
    try {
      const res = await fetch(endpoint);
      if (!res.ok) return null;
      initial = await res.json();
    } catch (_) { return null; }
    if (!_isRecord(initial) || typeof initial.enabled !== "boolean" || typeof initial.globalEnabled !== "boolean") return null;
    let enabled = initial.enabled;
    let globalEnabled = initial.globalEnabled;
    let saving = false;
    let confirmationOverlay: Element | null = null;
    const row = document.createElement("div");
    row.className = "ui-field";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ui-btn agent-btn agent-btn-cancel";
    const note = document.createElement("span");
    const render = () => {
      btn.textContent = enabled ? "이 방 자연 발화 켜짐" : "이 방 자연 발화 꺼짐";
      btn.setAttribute("aria-pressed", String(enabled));
      note.textContent = globalEnabled ? "" : "전체 꺼짐";
    };
    row.appendChild(btn);
    row.appendChild(note);
    render();
    setConfirmationOpen(() => confirmationOverlay?.isConnected === true);
    btn.addEventListener("click", () => {
      if (saving || btn.disabled || confirmationOverlay?.isConnected) return;
      const next = !enabled;
      showConfirmModal({
        title: next ? "이 방 자연 발화 켜기" : "이 방 자연 발화 끄기",
        body: next ? "이 방에서 에이전트가 호출 없이 대화에 한마디 보탤 수 있어요. 켤까요?" : "이 방에서 자연 발화를 멈춥니다. 끌까요?",
        confirmLabel: next ? "켜기" : "끄기",
        onConfirm: async () => {
          if (saving) return;
          saving = true;
          btn.disabled = true;
          errorP.textContent = "";
          let failureMessage = "자연 발화 변경에 실패했어요. 다시 시도해 주세요.";
          try {
            const res = await fetch(endpoint, {
              method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ enabled: next }),
            });
            const data: unknown = await res.json();
            if (!res.ok || !_isRecord(data) || typeof data.enabled !== "boolean" || typeof data.globalEnabled !== "boolean") {
              if (res.status === 403) failureMessage = "이 방 멤버만 자연 발화를 변경할 수 있어요.";
              throw new Error("switch_update_failed");
            }
            enabled = data.enabled;
            globalEnabled = data.globalEnabled;
            render();
          } catch (_) {
            errorP.textContent = failureMessage;
            errorP.removeAttribute("style");
          } finally {
            saving = false;
            btn.disabled = false;
          }
        },
      });
      confirmationOverlay = document.body.lastElementChild;
    });
    return row;
  }

  function _buildModal() {
    let confirmationOpen = () => false;
    const overlay = document.createElement("div");
    overlay.className = "plugin-restart-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");

    const modal = document.createElement("div");
    modal.className = "plugin-restart-modal";

    const header = document.createElement("div");
    header.className = "plugin-restart-modal-header";
    header.textContent = "참가자 목록";

    const body = document.createElement("div");
    body.className = "plugin-restart-modal-body";

    const errorP = document.createElement("p");
    errorP.className = "plugin-restart-warning";
    errorP.style.display = "none";

    const footer = document.createElement("div");
    footer.className = "plugin-restart-modal-footer";

    const closeBtn = document.createElement("button");
    closeBtn.className = "agent-btn agent-btn-cancel";
    closeBtn.textContent = "닫기";

    const leaveBtn = document.createElement("button");
    leaveBtn.className = "agent-btn agent-btn-save";
    leaveBtn.textContent = "방 나가기";

    footer.appendChild(closeBtn);
    footer.appendChild(leaveBtn);
    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    OverlayRegistry.register("room-members", close);

    function close() {
      overlay.remove();
      document.removeEventListener("keydown", onEscape);
      OverlayRegistry.unregister("room-members");
    }
    function onEscape(e: KeyboardEvent) {
      if (confirmationOpen()) return;
      if (e.key === "Escape") close();
    }
    closeBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close();
    });
    document.addEventListener("keydown", onEscape);

    return { body, errorP, footer, leaveBtn, close, setConfirmationOpen(check: () => boolean) { confirmationOpen = check; } };
  }

  async function open() {
    const room = await _resolveCurrentRoom();
    if (!room) return;

    const members = await _fetchMembers(room.id);
    const { body, errorP, footer, leaveBtn, close, setConfirmationOpen } = _buildModal();

    if (members.length === 0) {
      const empty = document.createElement("p");
      empty.textContent = "참가자 정보를 불러올 수 없어요";
      body.appendChild(empty);
    } else {
      members.forEach((member) => body.appendChild(_renderMemberRow(member)));
    }
    body.appendChild(errorP);

    // Phase 17: 방 나가기 옆에 음소거 토글 배치(§설계). 현재 상태는 이미 불러온 members에서
    // 본인(viewerActorId, ws-render-helpers.ts 전역)의 mutedAt으로 판정한다 — 별도 왕복 없음.
    const selfActorId = typeof viewerActorId === "function" ? viewerActorId() : null;
    const selfMember = members.find((member) => member.actorId === selfActorId);
    const muteBtn = _renderMuteToggle(room.id, Boolean(selfMember?.mutedAt));
    footer.insertBefore(muteBtn, leaveBtn);

    leaveBtn.addEventListener("click", async () => {
      errorP.style.display = "none";
      try {
        await withLoadingFeedback(leaveBtn, () => _leaveRoom(room.id), {
          loadingText: "나가는 중...",
        });
        close();
        if (typeof RoomList !== "undefined") RoomList.refresh();
        if (typeof renderChatHeader === "function") renderChatHeader(null);
      } catch (err) {
        console.error("[room-members] 나가기 실패:", err);
        errorP.textContent = "방 나가기에 실패했어요. 다시 시도해 주세요.";
        errorP.style.display = "";
      }
    });

    if (room.roomType === "group") {
      const toggle = await _renderNaturalSpeechToggle(room.id, errorP, setConfirmationOpen);
      if (toggle) footer.insertBefore(toggle, leaveBtn);
    }
  }

  return { open };
})();

document.addEventListener("DOMContentLoaded", () => {
  const header = document.getElementById("chatHeader");
  if (header) {
    header.style.cursor = "pointer";
    header.addEventListener("click", () => RoomMembers.open());
  }
});
